"use client";

import {
  memo,
  useEffect,
  useRef,
  useState,
  type JSX,
  type MouseEvent as ReactMouseEvent,
} from "react";
import Image from "next/image";
import {
  AppPlaceholder,
  Text,
  useSitecore,
  type LinkField,
} from "@sitecore-content-sdk/nextjs";
import { SitecoreLink as Link } from "../../../atoms/SitecoreLink";
import {
  BreadCrumbNavField,
  BreadCrumbNavProps,
  NavigationFields,
  NavigationProps,
  UtilityNavFields,
  UtilityNavProps,
} from "components/sxa/navigation/navigation.props";
import Icon from "../../../atoms/Icon";
import { ICONS } from "../../../variables/icons";
import useBreakpoint from "../../../hooks/useBreakpoint";
import { extractHiddenBlock } from "../../../utils/styles";
import { NoDataFallback } from "../../../utils/NoDataFallback";
import { useArticleContext } from "contexts/ArticleContext";
import { useNewsContext } from "contexts/NewsContext";
import { useStoryContext } from "contexts/StoriesContext";
import { useLocationContext } from "contexts/LocationContext";
import { useEventContext } from "contexts/EventContext";
import styles from "./Navigation.module.scss";

type ComponentMap = ReturnType<typeof useSitecore>["componentMap"];

type PlaceholderComponentProps = {
  rendering?: UtilityNavProps["rendering"];
  placeholderName?: string;
  page: UtilityNavProps["page"];
  componentMap: ComponentMap;
};

const getNavigationText = (
  props: NavigationProps,
): JSX.Element | string | undefined => {
  if (props.fields.NavigationTitle) {
    return <Text field={props.fields.NavigationTitle} />;
  }
  if (props.fields.Title) {
    return <Text field={props.fields.Title} />;
  }
  return props.fields.DisplayName;
};

const getLinkTitle = (props: NavigationProps): string | undefined => {
  if (props.fields.NavigationTitle?.value) {
    return props.fields.NavigationTitle.value.toString();
  }
  if (props.fields.Title?.value) {
    return props.fields.Title.value.toString();
  }
  return props.fields.DisplayName;
};

const getLinkField = (props: NavigationProps): LinkField => ({
  value: {
    href: props.fields.Href,
    title: getLinkTitle(props),
    querystring: props.fields.Querystring,
  },
});

const HeaderPlaceholder = memo(
  ({
    rendering,
    placeholderName,
    page,
    componentMap,
  }: PlaceholderComponentProps) => {
    const resolvedPlaceholderName = placeholderName || "utilitynavlogo-4";

    if (!rendering) return null;

    return (
      <AppPlaceholder
        name={resolvedPlaceholderName}
        rendering={rendering}
        page={page}
        componentMap={componentMap}
      />
    );
  },
);
HeaderPlaceholder.displayName = "HeaderPlaceholder";

const LanguagePlaceholder = memo(
  ({
    rendering,
    placeholderName,
    page,
    componentMap,
  }: PlaceholderComponentProps) => {
    if (!rendering || !placeholderName) return null;

    return (
      <AppPlaceholder
        name={placeholderName}
        rendering={rendering}
        page={page}
        componentMap={componentMap}
      />
    );
  },
);
LanguagePlaceholder.displayName = "LanguagePlaceholder";

const SigninPlaceholder = memo(
  ({
    rendering,
    placeholderName,
    page,
    componentMap,
  }: PlaceholderComponentProps) => {
    if (!rendering || !placeholderName) return null;

    return (
      <AppPlaceholder
        name={placeholderName}
        rendering={rendering}
        page={page}
        componentMap={componentMap}
      />
    );
  },
);
SigninPlaceholder.displayName = "SigninPlaceholder";

const NavigationList = (props: NavigationProps): JSX.Element => {
  const { page } = props;
  const [active, setActive] = useState(false);
  const { isDesktop } = useBreakpoint();
  const { Styles = [] } = props.fields || {};
  const classNameList = `${Styles.concat("rel-level" + props.relativeLevel).join(" ")}`;

  const children: JSX.Element[] = props.fields.Children?.length
    ? props.fields.Children.map((element: NavigationFields, index: number) => (
        <NavigationList
          key={`${index}${element.Id}`}
          page={page}
          rendering={props.rendering}
          params={props.params}
          fields={element}
          handleClick={props.handleClick}
          relativeLevel={props.relativeLevel + 1}
          variant={props.variant}
        />
      ))
    : [];

  const shouldOpenInNewTab = props.variant === "utilitynavigation";

  const utilityNavClass =
    "flex items-center font-normal focus:font-medium xl:hover:bg-[var(--hover-effect-opacity)] xl:leading-[17.6px] xl:px-4 xl:pb-[11px] xl:pt-[15px] pb-[25px] hover:underline active:underline focus:underline xl:active:no-underline xl:focus:no-underline xl:hover:no-underline";

  const breadcrumbClass =
    "text-sm text-dark-gray underline hover:text-ecolab-blue focus:text-black active:no-underline leading-7";

  let variantClass = "";

  if (props.variant === "utilitynavigation") {
    variantClass = utilityNavClass;
  } else if (props.variant === "breadcrumb") {
    variantClass = breadcrumbClass;
  }

  const showIcon =
    (props.variant === "utilitynavigation" && !isDesktop) ||
    props.variant === "breadcrumb";

  let iconContent = null;

  if (props.variant === "breadcrumb") {
    iconContent = (
      <span className="mx-2 text-blue-700">
        <Icon icon={ICONS.CHEVRON_RIGHT} className="w-4 h-4" />
      </span>
    );
  } else if (props.variant === "utilitynavigation") {
    iconContent = (
      <Icon icon={ICONS.CHEVRON_WHITE_RIGHT} className="w-4 h-4 ml-2" />
    );
  }

  return (
    <li
      className={`${classNameList} ${active ? "active" : ""}`}
      key={props.fields.Id}
    >
      <div
        className={`navigation-title ${children.length ? "child" : ""}`}
        onClick={() => setActive((prev) => !prev)}
      >
        <Link
          field={getLinkField(props)}
          editable={page.mode.isEditing}
          onClick={props.handleClick}
          className={variantClass}
          tabIndex={0}
          data-role="link"
          target={shouldOpenInNewTab ? "_blank" : undefined}
          rel={shouldOpenInNewTab ? "noopener noreferrer" : undefined}
        >
          {getNavigationText(props)}
          {showIcon ? iconContent : null}
        </Link>
      </div>
      {children.length > 0 && <ul className="clearfix">{children}</ul>}
    </li>
  );
};

export const Default = (props: NavigationProps): JSX.Element => {
  console.log("---->[Navigation] Component", props.fields);

  const { page, rendering, params, fields } = props;
  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const wrapperStyles = params
    ? `${params?.GridParameters ?? ""} ${params?.Styles ?? ""}`.trimEnd()
    : "";
  const id = params?.RenderingIdentifier ?? null;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  if (!fields || !Object.values(fields).length) {
    return <NoDataFallback componentName="Navigation" />;
  }

  const handleToggleMenu = (
    event?: ReactMouseEvent<HTMLElement>,
    flag?: boolean,
  ): void => {
    if (event && page.mode.isEditing) {
      event.preventDefault();
    }
    if (flag !== undefined) {
      setIsOpenMenu(flag);
      return;
    }
    setIsOpenMenu(!isOpenMenu);
  };

  const list = Object.values(fields)
    .filter(Boolean)
    .map((element: NavigationFields, key: number) => (
      <NavigationList
        key={`${key}${element.Id}`}
        page={page}
        rendering={rendering}
        params={params}
        fields={element}
        handleClick={(event: ReactMouseEvent<HTMLElement>) =>
          handleToggleMenu(event, false)
        }
        relativeLevel={1}
      />
    ));

  return (
    <div
      className={`navigation-component  ${wrapperStyles} ${hiddenBlockStyles}`}
      id={id || undefined}
      data-testid="default-navigation"
      data-role="navigation-component"
    >
      <label
        className="menu-mobile-navigate-wrapper"
        aria-label="Toggle mobile navigation"
      >
        <input
          type="checkbox"
          className="menu-mobile-navigate"
          checked={isOpenMenu}
          onChange={() => handleToggleMenu()}
          data-testid="checkbox"
        />
        <div className="menu-humburger" />
        <div className="component-content">
          <nav>
            <ul className="clearfix">{list}</ul>
          </nav>
        </div>
      </label>
    </div>
  );
};

export const UtilityNavigation = (props: UtilityNavProps): JSX.Element => {
  console.log("---->[Navigation] Component", props.fields);

  const { page, rendering, params, fields } = props;
  // Read componentMap from SitecoreProvider context instead of importing it.
  // Static `import componentMap from '.sitecore/component-map'` creates a
  // load-time cycle (component-map.ts spreads `{...Navigation}`, hitting
  // TDZ on BreadcrumbNavigation / ChBreadcrumbNavigation).
  const { componentMap } = useSitecore();
  const { isDesktop, isMobile, isTablet } = useBreakpoint();

  const utilityNavRef = useRef<HTMLDivElement>(null);
  const desktopSlotRef = useRef<HTMLDivElement>(null);
  const mobileSlotRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const placeholderKeys = Object.keys(rendering?.placeholders || {});
  const headerPlaceholder = placeholderKeys.find((key) =>
    key.startsWith("utilitynavlogo"),
  );
  const languagePlaceholder = placeholderKeys.find((key) =>
    key.startsWith("languagepicker"),
  );
  const signinPlaceholder = placeholderKeys.find((key) =>
    key.startsWith("utilitynavsignin"),
  );

  const listUlStyle = isExpanded
    ? "transition-all duration-500 ease-in-out max-h-[500px] opacity-100 overflow-hidden"
    : "transition-all duration-500 ease-in-out max-h-0 opacity-0 overflow-hidden";
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  // Collapse the mobile drawer if user resizes up to desktop while open.
  // Adjust state during render rather than in an effect so we avoid an
  // extra commit (see https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes).
  const [prevIsDesktop, setPrevIsDesktop] = useState(isDesktop);
  if (isDesktop !== prevIsDesktop) {
    setPrevIsDesktop(isDesktop);
    if (isDesktop && isExpanded) {
      setIsExpanded(false);
    }
  }

  const handleToggle = () => {
    if (!isDesktop) {
      setIsExpanded((prev) => !prev);
    }
  };

  const list = Object.values(fields)
    .filter(Boolean)
    .map((element: UtilityNavFields, key: number) => (
      <NavigationList
        key={`${key}-${element.Id}`}
        page={page}
        rendering={rendering}
        params={params}
        fields={element as unknown as NavigationFields}
        relativeLevel={1}
        variant="utilitynavigation"
      />
    ));

  // The SSO sign-in script injects its container into <body>; re-parent it
  // into our desktop or mobile slot depending on viewport.
  useEffect(() => {
    const injected = utilityNavRef?.current?.querySelector(
      ".sso-sign-in-container, .customer-sign-in-container",
    );

    if (!injected) return;

    const target = isDesktop ? desktopSlotRef.current : mobileSlotRef.current;
    if (!target || target.querySelector(".user-info")) return;

    target.appendChild(injected);
  }, [isDesktop, isTablet, isMobile]);

  return (
    <div ref={utilityNavRef}>
      <div
        className={`utilitynavigation-component flex items-center justify-between xl:pl-8 xl:pr-4 px-4 ${
          isExpanded ? "shadow-[0_2px_24px_0_rgba(0,0,0,0.5)] " : ""
        } max-w-[1920px] h-[44px] min-h-[44px] ${hiddenBlockStyles}`}
        data-role="navigation-component"
      >
        <div className="flex min-h-[44px]">
          <div
            className="logo-focus-img flex content-center"
            data-testid="utilitynavlogo-4"
          >
            <div className="w-[121px] min-h-[44px] [&>div]:max-h-[44px] [&_img]:max-h-[14px] [&_img]:object-contain [&>div]:mb-0 [&_img]:rounded-none [&>div>a]:px-6 [&>div]:flex [&>div>a]:pt-[14px] [&>div>a]:pb-[16px] [&>div>img]:px-6 [&>div>img]:pt-[14px] [&>div>img]:pb-[16px]">
              <HeaderPlaceholder
                rendering={rendering}
                placeholderName={headerPlaceholder}
                page={page}
                componentMap={componentMap}
              />
            </div>
          </div>
        </div>

        <div
          className={`flex items-center text-white min-w-[240px] min-h-[40px] ${
            isDesktop ? "" : "hidden"
          }`}
          data-testid="languagepicker-4"
        >
          <ul className="flex min-h-[40px]">
            {rendering && languagePlaceholder && (
              <li className="flex">
                <LanguagePlaceholder
                  rendering={rendering}
                  placeholderName={languagePlaceholder}
                  page={page}
                  componentMap={componentMap}
                />
              </li>
            )}
            {list}
            <li className="flex">
              <div ref={desktopSlotRef}>
                <SigninPlaceholder
                  rendering={rendering}
                  placeholderName={signinPlaceholder}
                  page={page}
                  componentMap={componentMap}
                />
              </div>
            </li>
          </ul>
        </div>
        <button
          onClick={handleToggle}
          aria-label="Settings Toggle"
          data-testid="settings-toggle"
          className={`px-[6px] py-[1px] ${isDesktop ? "hidden" : ""}`}
        >
          <Image
            src={isExpanded ? "/icon-gear-close.svg" : "/icon-gear-open.svg"}
            alt="Settings"
            width={28}
            height={32}
            className="!w-[28px] !h-[32px] object-contain"
          />
        </button>
      </div>

      <div className={`${listUlStyle} ${isDesktop ? "hidden" : ""}`}>
        <div
          className={`w-full bg-dark-blue text-white ${styles.navigationGradientBg} px-[15px] pt-[25px] mb-auto`}
          data-testid="languagepicker-4"
        >
          <ul>
            <li className="flex items-center pb-[25px] active:underline focus:underline">
              {rendering && languagePlaceholder && (
                <div className="flex flex-col gap-1">
                  <LanguagePlaceholder
                    rendering={rendering}
                    placeholderName={languagePlaceholder}
                    page={page}
                    componentMap={componentMap}
                  />
                </div>
              )}
            </li>
            {list}
            <li className="flex">
              <div ref={mobileSlotRef} />
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const findActivePath = (
  items: BreadCrumbNavField[],
): BreadCrumbNavField[] | null => {
  for (const item of items) {
    if (item?.Styles?.includes("active")) {
      return [item];
    }
    if (item.Children && item.Children.length > 0) {
      const childPath = findActivePath(item.Children);
      if (childPath) {
        return [item, ...childPath];
      }
    }
  }
  return null;
};

const getBreadcrumbLinkField = (item: BreadCrumbNavField) => ({
  value: {
    href: item.Href,
    text: item.NavigationTitle?.value,
    title: item.NavigationTitle?.value,
    linktype: "internal",
  },
});

export const BreadcrumbNavigation = (
  props: BreadCrumbNavProps,
): JSX.Element | null => {
  console.log("---->[Navigation] Component", props.fields);

  const { params, fields } = props;
  const id = params?.RenderingIdentifier;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  const breadcrumbItemsMap = (fields ?? {}) as {
    [key: string]: BreadCrumbNavField;
  };
  const breadcrumbItems: BreadCrumbNavField[] =
    Object.values(breadcrumbItemsMap);
  const activePath = Array.isArray(breadcrumbItems)
    ? (findActivePath(breadcrumbItems) ?? [])
    : [];

  if (activePath.length === 0) return null;

  return (
    <div
      data-testid="breadcrumb-navigation"
      className={`component breadcrumb-navigation ${
        params?.Styles ?? ""
      } py-6 px-[var(--spacing-sixteen)] ${hiddenBlockStyles}`}
      id={id}
      data-role="navigation-component"
    >
      <div className="w-full">
        <div className="max-w-screen-xl">
          <nav aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center text-sm text-gray-700 sm:ml-[-6px] md:ml-[0px]">
              {activePath.map((item, index) => {
                const isFirst = index === 0;
                const isLast = index === activePath.length - 1;
                const isPrev = index === activePath.length - 2;
                return (
                  <li
                    key={item.Id}
                    className={`flex items-center ${isPrev ? "block" : "hidden md:flex"}`}
                  >
                    {!isLast ? (
                      <>
                        {isPrev && !isFirst && (
                          <span className="text-ecolab-blue md:hidden">
                            <Icon icon={ICONS.CHEVRON_LEFT} />
                          </span>
                        )}
                        <Link
                          field={getBreadcrumbLinkField(item)}
                          className="underline text-dark-gray hover:text-ecolab-blue focus:text-black focus-visible:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus:no-underline focus:outline-none active:no-underline active:outline-none active:text-black cursor-pointer text-base leading-7 font-normal"
                          data-role="link"
                        >
                          {item.NavigationTitle?.value}
                        </Link>
                        <span className="mx-2 text-ecolab-blue hidden md:inline">
                          <Icon icon={ICONS.CHEVRON_RIGHT} />
                        </span>
                      </>
                    ) : (
                      <span
                        className="text-black text-base leading-7"
                        aria-current="page"
                      >
                        <Text field={item.NavigationTitle} />
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>
      </div>
    </div>
  );
};

export const ChBreadcrumbNavigation = (
  props: BreadCrumbNavProps,
): JSX.Element | null => {
  console.log("---->[Navigation] Component", props.fields);

  const { params, fields } = props;
  const id = params?.RenderingIdentifier;

  const breadcrumbItemsMap = (fields ?? {}) as {
    [key: string]: BreadCrumbNavField;
  };
  const breadcrumbItems: BreadCrumbNavField[] =
    Object.values(breadcrumbItemsMap);
  const activePath = Array.isArray(breadcrumbItems)
    ? (findActivePath(breadcrumbItems) ?? [])
    : [];

  // Read CH contexts unconditionally — hooks rule. Title resolution below.
  const { article } = useArticleContext();
  const { news } = useNewsContext();
  const { event } = useEventContext();
  const { story } = useStoryContext();
  const { location } = useLocationContext();

  // Bug fix: original computed `pageNavigationTitle` from
  // `activePath[activePath.length - 1]` BEFORE the empty-path guard, which
  // crashed on empty paths. Guard first.
  if (activePath.length === 0) return null;

  let pageNavigationTitle = activePath.at(-1)?.NavigationTitle?.value;

  if (article) {
    pageNavigationTitle =
      article.MetaTitle || article.OGTitle || article.ArticleTitle;
  }
  if (news) {
    pageNavigationTitle = news.MetaTitle || news.OGTitle || news.NewsTitle;
  }
  if (event) {
    pageNavigationTitle = event.MetaTitle || event.EventTitle;
  }
  if (story) {
    pageNavigationTitle = story.MetaTitle || story.OGTitle || story.StoryTitle;
  }
  if (location) {
    pageNavigationTitle =
      location.MetaTitle || location.OGTitle || location.LocationName;
  }

  return (
    <div
      data-testid="breadcrumb-navigation"
      className={`component breadcrumb-navigation ${
        params?.Styles ?? ""
      } py-6 px-[var(--spacing-sixteen)]`}
      id={id}
      data-role="navigation-component"
    >
      <div className="w-full">
        <div className="max-w-screen-xl">
          <nav aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center text-sm text-gray-700 sm:ml-[-6px] md:ml-[0px]">
              {activePath.map((item, index) => {
                const isLast = index === activePath.length - 1;
                const isPrev = index === activePath.length - 2;
                return (
                  <li
                    key={item.Id}
                    className={`flex items-center ${isPrev ? "block" : "hidden md:flex"}`}
                  >
                    {!isLast ? (
                      <>
                        {isPrev && (
                          <span className="text-ecolab-blue md:hidden">
                            <Icon icon={ICONS.CHEVRON_LEFT} />
                          </span>
                        )}
                        <Link
                          field={getBreadcrumbLinkField(item)}
                          className="underline text-dark-gray hover:text-ecolab-blue focus:text-black focus-visible:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus:no-underline focus:outline-none active:no-underline active:outline-none active:text-black cursor-pointer text-base leading-7 font-normal"
                          data-role="link"
                        >
                          {item.NavigationTitle?.value}
                        </Link>
                        <span className="mx-2 text-ecolab-blue hidden md:inline">
                          <Icon icon={ICONS.CHEVRON_RIGHT} />
                        </span>
                      </>
                    ) : (
                      <span
                        className="text-black text-base leading-7"
                        aria-current="page"
                      >
                        {pageNavigationTitle}
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>
      </div>
    </div>
  );
};
